import { FormattedMessage, useIntl } from "react-intl";
import { CandidateAssessmentPromptEditor } from "@/components/match/CandidateAssessmentPromptEditor";
import { CompanyRequirementsPanel } from "@/components/match/requirements/CompanyRequirementsPanel";
import { RoleRequirementsPanel } from "@/components/match/requirements/RoleRequirementsPanel";
import { PowersPicker } from "@/components/match/PowersPicker";
import { Disclosure } from "@/components/design/Disclosure";
import type { MatchData } from "@/components/match/match-design-types";
import type { DashboardContext } from "@/pages/project/dashboard-kinds";
import { listingSearchTarget } from "./listing-search-target";
import { listingRequirementsData, listingRequirementsDesign } from "./listing-requirements";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { useAssistantContext } from "@/lib/use-assistant-context";

export function ListingRequirements({ ctx }: { ctx: DashboardContext }) {
  const intl = useIntl();
  const data = listingRequirementsData(ctx.data);
  const company = listingSearchTarget(ctx.data) === "COMPANIES";
  function update(patch: Partial<MatchData>) {
    if (!ctx.readOnly) ctx.editData({ matchRequirements: listingRequirementsDesign({ ...data, ...patch }) });
  }
  useAssistantContext([{ description: "listing requirements source", value:
    `Listing ${ctx.entity.id}; ${company ? "companies" : "people"}; requirements are edited here; linked match versions are immutable snapshots.` }]);
  return <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="text-smycles-title font-medium"><FormattedMessage id="share.listings.requirements.heading" /></h3>
      {!ctx.readOnly && <AskAssistantButton
        onBeforeAsk={ctx.flush}
        action={company ? undefined : "GENERATE_MATCH_REQUIREMENTS"}
        label={<FormattedMessage id="match.challenge.generateWithAi" />}
        prompt={intl.formatMessage({ id: company ? "share.listings.requirements.companyPrompt" : "share.listings.requirements.peoplePrompt" })}
      />}
    </div>
    <CandidateAssessmentPromptEditor data={data} update={update} readOnly={ctx.readOnly} mode={company ? "RANK_COMPANIES" : "RANK"} />
    <div className={company ? "" : "grid items-start gap-6 @5xl:grid-cols-2"}>
      {company ? <CompanyRequirementsPanel data={data} update={update} readOnly={ctx.readOnly} />
        : <>
          <RoleRequirementsPanel data={data} update={update} readOnly={ctx.readOnly} />
          <Disclosure defaultOpen title={<FormattedMessage id="project.guided.team.powersTitle" />}>
            <PowersPicker profile={data.teamProfile} readOnly={ctx.readOnly} id="listing-team-profile" editorIdPrefix="listing-powers"
              onChange={teamProfile => update({ teamProfile })} />
          </Disclosure>
        </>}
    </div>
  </div>;
}
